import {
	Box,
	Card,
	CardContent,
	Chip,
	IconButton,
	Skeleton,
	Tooltip,
	Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import {
	getRefund,
	getRefundBreakdown,
	getRefundBreakdownLoader,
	getRefundLoader,
} from 'redux/reducers/refunds/selectors';
import {
	getConfirmedOrders,
	getOrderDetailsCombined,
	getOrderDetailsCombinedLoader,
} from 'redux/reducers/orders/selectors';
import AddBoxIcon from '@mui/icons-material/AddBox';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { nanoid } from '@reduxjs/toolkit';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { toNumber } from 'utils/toNumber';
import { RefundBreakdownItem } from './RefundBreakdownItem';
import { TOrderDetailsCombinedReturn } from 'redux/reducers/orders/types';
import { COLORS } from 'services/constants/colors';

export const RefundItemsBreakdown: FC<{ refundRow: TOrderDetailsCombinedReturn }> = ({
	refundRow,
}) => {
	const orderDetailsCombinedIsLoading = useSelector(getOrderDetailsCombinedLoader);

	const [detalization, setDetalization] = useState(false);
	const handleDetalization = () => {
		setDetalization(!detalization);
	};

	// const refundRow = orderDetailsCombined?.orderReturns?.[0];
	const paymentCurrency = refundRow?.paymentCurrency ?? 'EUR';
	const breakdownRows = refundRow?.orderReturnItems ?? [];

	return (
		<div>
			{!!refundRow && !orderDetailsCombinedIsLoading && (
				<Card
					sx={{
						mb: 2,
						'&': {
							borderRadius: '12px',
						},
					}}
				>
					<CardContent>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
							<Typography sx={{ fontSize: 14 }} color="primary" gutterBottom>
								Return Items breakdown
							</Typography>
							{detalization ? (
								<Tooltip title="Hide more details">
									<IconButton size="small" color="primary" onClick={handleDetalization}>
										<IndeterminateCheckBoxIcon />
									</IconButton>
								</Tooltip>
							) : (
								<Tooltip title="Show more details">
									<IconButton size="small" color="primary" onClick={handleDetalization}>
										<AddBoxIcon />
									</IconButton>
								</Tooltip>
							)}
						</Box>

						<Box
							sx={{
								border: '1px solid #00000020',
								borderRadius: '12px',
								padding: '10px',
								marginBottom: '10px',
							}}
						>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'row',
									justifyContent: 'start',
									alignItems: 'center',
									gap: 1,
									mb: 2,
								}}
							>
								<Typography variant="body2" color="text.secondary">
									{refundRow?.returnCreateDate &&
										DateTime.fromISO(refundRow.returnCreateDate).toFormat('yyyy-MM-dd, HH:mm')}
								</Typography>
								{!!refundRow?.status && (
									<Chip
										label={refundRow.status.toLowerCase().replaceAll('_', ' ')}
										size="small"
										sx={{
											p: '0 5px',
											ml: 1,
											fontSize: '.7rem',
											height: '20px',
											bgcolor: COLORS.SECONDARY,
										}}
									/>
								)}
							</Box>

							{breakdownRows.map((item) => (
								<RefundBreakdownItem
									key={nanoid()}
									refundBreakdownItem={item}
									detalization={detalization}
									paymentCurrency={paymentCurrency}
								/>
							))}

							{breakdownRows.length > 1 && (
								<Box sx={{ display: 'flex', justifyContent: 'end', mt: 1 }}>
									<AmountAndCurrency
										amount={toNumber(refundRow?.totalReturnAmount)}
										currency={paymentCurrency || 'EUR'}
										bold
										size={1}
									/>
								</Box>
							)}
						</Box>
					</CardContent>
				</Card>
			)}
			{orderDetailsCombinedIsLoading && (
				<Skeleton variant="rectangular" height="300px" sx={{ borderRadius: '12px', mb: 2 }} />
			)}
		</div>
	);
};
